import Link from 'next/link';
import React, { ComponentProps, useState } from 'react';
import Modal from 'react-modal';
import { useTranslation } from 'utils/i18n';

import CheckboxBase from '../fields/checkbox';

const Checkbox = CheckboxBase as unknown as React.FC<
  ComponentProps<typeof CheckboxBase> &
    React.InputHTMLAttributes<HTMLInputElement>
>;

interface Props {
  isOpen: boolean;
  onClose: Function;
}

const AcceptConditionsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const [privacyPolicyAgreed, togglePrivacyPolicyAgreement] = useState(false);
  const [termsAgreed, toggleTermsAgreement] = useState(false);

  const agreementLabelContent = (text: string, href: string) => (
    <>
      {t('registration.service_details.i_have_read_and_agree_to')}
      &nbsp;
      <Link href={href} target="_blank">
        {text}
      </Link>
    </>
  );

  const handleConfirm = () => {
    onClose();
  };

  const conditionsAccepted = termsAgreed && privacyPolicyAgreed;

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Accept Conditions Modal"
      className="modal modal--small accept-conditions-modal"
      overlayClassName="modal__overlay"
      shouldCloseOnOverlayClick={false}
    >
      <div className="accept-conditions-modal__header">
        <h4 className="accept-conditions-modal-title">
          <i className="fas fa-exclamation-triangle" />
          {t('accept_conditions_modal.title')}
        </h4>
      </div>
      <div className="accept-conditions-modal__inner">
        <h2>{t('accept_conditions_modal.subtitle')}</h2>
        <p className="accept-conditions-modal__inner-intro-text">
          {t('accept_conditions_modal.body')}
        </p>
        <p>
          <Checkbox
            label={agreementLabelContent(
              t('registration.service_details.fields.agree_privacy.label'),
              t('navigation.privacy_policy.url')
            )}
            name="agreePrivacyPolicy"
            id="agreePrivacyPolicy"
            checked={privacyPolicyAgreed}
            onChange={() => {
              togglePrivacyPolicyAgreement(!privacyPolicyAgreed);
            }}
          />
          <Checkbox
            label={agreementLabelContent(
              t('registration.service_details.fields.agree_terms.label'),
              t('navigation.terms_and_conditions.url')
            )}
            name="agreeTerms"
            id="agreeTerms"
            checked={termsAgreed}
            onChange={() => {
              toggleTermsAgreement(!termsAgreed);
            }}
          />
        </p>

        <p>
          <button
            type="button"
            onClick={handleConfirm}
            className="button button--secondary"
            disabled={!conditionsAccepted}
            data-testid="acceptConditionsButton"
          >
            {t('accept_conditions_modal.confirm')}
          </button>
        </p>
      </div>
    </Modal>
  );
};

export default AcceptConditionsModal;
